import { useState, type FormEvent } from 'react';
import type { ClientConfig } from '@ferro/config';
import { requestStaffPasswordReset } from '../lib/api.js';

// #405 — staff forgot-password request page. Mirrors the visual pattern
// of InviteAccept.tsx (same `ferro-admin-login` / `ferro-auth` / `ferro-form`
// classes) so the auth-recovery surface reads as part of the same family
// as invite-accept and login, not a bolted-on page.
//
// Always shows the SAME success state regardless of whether the typed
// email matched an account — the server's response is enumeration-safe
// by design (staff-forgot-password.ts), and the UI must not undermine
// that by showing a different state for "found" vs "not found".

type StaffForgotPasswordProps = {
  config: ClientConfig;
};

type UiState =
  | { kind: 'ready' }
  | { kind: 'submitting' }
  | { kind: 'sent' }
  | { kind: 'rate_limited' };

export function StaffForgotPassword({ config }: StaffForgotPasswordProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<UiState>({ kind: 'ready' });

  const formReady = email.trim().length > 0 && state.kind === 'ready';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formReady) return;
    setState({ kind: 'submitting' });
    try {
      await requestStaffPasswordReset(config.deployment.apiUrl, { email: email.trim() });
      setState({ kind: 'sent' });
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      if (status === 429) {
        setState({ kind: 'rate_limited' });
        return;
      }
      // Any other failure (network, 400 malformed email, 5xx) still
      // resolves to the SAME enumeration-safe "sent" copy — the only
      // observable difference a user should ever see is rate-limiting,
      // never account existence.
      setState({ kind: 'sent' });
    }
  };

  if (state.kind === 'sent') {
    return (
      <main className="ferro-admin-login">
        <section className="ferro-auth">
          <h1 className="ferro-auth__title">Check your email</h1>
          <p className="ferro-auth__subtitle">
            If a {config.brand.name} staff account exists for that email, we
            sent a link to reset your password. It expires in 30 minutes.
          </p>
          <a href="/login" className="ferro-btn ferro-btn--primary ferro-btn--block">
            Back to sign in
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="ferro-admin-login">
      <section className="ferro-auth">
        <h1 className="ferro-auth__title">Reset your password</h1>
        <p className="ferro-auth__subtitle">
          Enter your {config.brand.name} staff account email and we'll send
          you a link to reset your password.
        </p>
        <form className="ferro-form" onSubmit={handleSubmit} noValidate>
          <label className="ferro-field">
            <span className="ferro-field__label">Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state.kind === 'submitting'}
              className="ferro-field__input"
            />
          </label>
          {state.kind === 'rate_limited' ? (
            <p className="ferro-form__error" role="alert">
              Too many password-reset requests. Please try again in a few
              minutes.
            </p>
          ) : null}
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary ferro-btn--block"
            disabled={!formReady}
          >
            {state.kind === 'submitting' ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <p className="ferro-auth__hint">
          <a href="/login">Back to sign in</a>
        </p>
      </section>
    </main>
  );
}
